import {
  buildTemplateParameters,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  replaceTemplateVariables,
} from 'dashboard/helper/templateHelper';

const componentType = component => component.type?.toUpperCase();

const messageTemplateParams = attributes =>
  attributes.templateParams || attributes.template_params || {};

const messageTemplateComponents = (attributes, templateParams) =>
  attributes.templateComponents ||
  attributes.template_components ||
  templateParams.templateComponents ||
  templateParams.template_components ||
  [];

const messageProcessedParams = templateParams =>
  templateParams.processedParams || templateParams.processed_params || {};

export class WhatsAppTemplateDocument {
  constructor({ template = {}, processedParams = {}, attachments = [] } = {}) {
    this.template = template;
    this.processedParams = processedParams;
    this.attachments = attachments;
  }

  static fromTemplate(template, processedParams = {}) {
    return new WhatsAppTemplateDocument({ template, processedParams });
  }

  static fromMessage(attributes = {}, attachments = []) {
    const templateParams = messageTemplateParams(attributes);
    const template = {
      ...templateParams,
      components: messageTemplateComponents(attributes, templateParams),
    };
    return new WhatsAppTemplateDocument({
      template,
      processedParams: messageProcessedParams(templateParams),
      attachments,
    });
  }

  component(type) {
    return (this.template.components || []).find(
      component => componentType(component) === type
    );
  }

  get header() {
    return this.component(COMPONENT_TYPES.HEADER);
  }

  get body() {
    return this.component(COMPONENT_TYPES.BODY);
  }

  get footer() {
    return this.component('FOOTER');
  }

  get buttonsComponent() {
    return this.component(COMPONENT_TYPES.BUTTONS);
  }

  get headerText() {
    return this.header?.format === 'TEXT' ? this.header.text : '';
  }

  get bodyText() {
    return this.body?.text || '';
  }

  get footerText() {
    return this.footer?.text || '';
  }

  get buttons() {
    return this.buttonsComponent?.buttons || [];
  }

  get mediaFormat() {
    return this.header?.format || '';
  }

  get hasMediaHeader() {
    return MEDIA_FORMATS.includes(this.mediaFormat);
  }

  get isImage() {
    return this.mediaFormat.toLowerCase() === 'image';
  }

  get isDocument() {
    return this.mediaFormat.toLowerCase() === 'document';
  }

  get hasVariables() {
    return /{{([^}]+)}}/.test(this.bodyText);
  }

  get renderedBody() {
    return replaceTemplateVariables(this.bodyText, this.processedParams);
  }

  get mediaPreview() {
    return this.header?.example?.header_handle?.[0] || '';
  }

  get imageUrl() {
    const image = this.attachments.find(
      attachment => (attachment.fileType || attachment.file_type) === 'image'
    );
    const header = this.processedParams.header || {};
    return (
      image?.dataUrl ||
      image?.data_url ||
      header.mediaUrl ||
      header.media_url ||
      ''
    );
  }

  initialParameters({ defaultImageUrl = '' } = {}) {
    const parameters = buildTemplateParameters(
      this.template,
      this.hasMediaHeader
    );
    if (this.isImage && defaultImageUrl) {
      parameters.header ??= {};
      parameters.header.media_url = defaultImageUrl;
    }
    return parameters;
  }

  isComplete({ mediaSignedId = '' } = {}) {
    if (!this.hasVariables && !this.hasMediaHeader) return true;
    if (
      this.hasMediaHeader &&
      !this.processedParams.header?.media_url &&
      !mediaSignedId
    ) {
      return false;
    }
    if (
      this.hasVariables &&
      this.processedParams.body &&
      Object.values(this.processedParams.body).some(value => !value)
    ) {
      return false;
    }
    if (this.processedParams.buttons?.some(button => !button.parameter)) {
      return false;
    }
    return true;
  }

  messagePayload() {
    const { name, category, language, namespace } = this.template;
    return {
      message: this.renderedBody,
      templateParams: {
        name,
        category,
        language,
        namespace,
        processed_params: this.processedParams,
        template_components: this.template.components || [],
      },
    };
  }

  get searchText() {
    return [
      this.template.name,
      this.template.category,
      this.template.language,
      this.header?.text,
      this.bodyText,
      this.footerText,
      ...this.buttons.map(button => button.text),
    ]
      .join(' ')
      .toLowerCase();
  }
}
